import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";

type Listener = (value: any) => void;

const listeners = new Map<string, Listener[]>();
const addListener = (key: string, listener: Listener) => {
  if (listeners.has(key)) {
    listeners.get(key)!.push(listener);
  } else {
    listeners.set(key, [listener]);
  }
};
const removeListener = (key: string, listener: Listener) => {
  if (listeners.has(key)) {
    listeners.set(
      key,
      listeners.get(key)!.filter((v) => v !== listener),
    );
  }
};
const callListeners = (key: string, value: any) => {
  if (listeners.has(key)) {
    for (const listener of listeners.get(key)!) {
      listener(value);
    }
  }
};
export default function useLocalStorageState<T extends string>(
  key: string,
  defaultValue: T,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(
    (localStorage.getItem(key) as T) ?? defaultValue,
  );
  useEffect(() => {
    if (value != null) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
    callListeners(key, value);
  }, [key, value]);
  useEffect(() => {
    const listener = (newValue: SetStateAction<T>) => {
      if (newValue !== value) {
        setValue(newValue);
      }
    };
    addListener(key, listener);
    return () => {
      removeListener(key, listener);
    };
  }, [key, value]);
  return [value, setValue];
}

export function useBooleanLocalStorageState(
  key: string,
  defaultValue: boolean,
): [boolean, Dispatch<SetStateAction<boolean>>] {
  const [value, setValue] = useLocalStorageState(key, `${defaultValue}`);
  const setBooleanValue = useCallback(
    (value) => {
      if (typeof value === "function") {
        setValue((oldValue) => value(oldValue?.toString() === "true"));
      } else {
        setValue(value);
      }
    },
    [setValue],
  );
  return [value?.toString() === "true", setBooleanValue];
}
